import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Container, Button, Alert, Card, Spinner } from "react-bootstrap";
import {
  loadRazorpayScript,
  createRenewalOrderNew,
  openRazorpayCheckout,
  processLifeRenewalPayment,
  processAssociateRenewalPayment,
  formatIndianCurrency
} from "../utils/razorpay";

const MembershipRenewalPayment = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  
  const {
    memberData,
    membershipType,
    paymentDetails,
    renewalInfo,
    acceptedTerms
  } = location.state || {};

  const baseUrl = process.env.REACT_APP_BASEURL;
  const user = JSON.parse(localStorage.getItem('user'));

  useEffect(() => {
    // Redirect if accessing directly or without accepting terms
    if (!acceptedTerms || !memberData || !paymentDetails) {
      navigate('/membership-renewal-terms');
    }
  }, [acceptedTerms, memberData, paymentDetails, navigate]);

  const handlePayment = async () => {
    try {
      setLoading(true);
      setError("");

      // Load Razorpay SDK
      await loadRazorpayScript();

      // Create renewal order using new backend endpoint
      const orderResponse = await createRenewalOrderNew({
        baseUrl,
        membershipId: memberData.membershipId,
        membershipType: membershipType === 'full' ? 'lifemembership' : 'associatemembership',
        notes: {
          renewal: true,
          memberId: memberData._id,
          breakdown: paymentDetails.breakdown
        }
      });

      // Open Razorpay checkout
      await openRazorpayCheckout({
        order: orderResponse,
        name: "AIOTA Membership Renewal",
        description: `${membershipType === 'full' ? 'Life' : 'Associate'} Membership Renewal`,
        prefill: {
          name: memberData.name,
          email: memberData.email,
          contact: memberData.phone
        },
        notes: {
          membershipId: memberData.membershipId,
          membershipType: membershipType,
          renewal: true
        },
        onSuccess: async (response) => {
          try {
            // Process renewal payment using appropriate endpoint
            let verifyResponse;
            if (membershipType === 'full' || membershipType === 'life') {
              verifyResponse = await processLifeRenewalPayment({
                baseUrl,
                membershipId: user.details_id,
                payload: response
              });
            } else {
              verifyResponse = await processAssociateRenewalPayment({
                baseUrl,
                membershipId: user.details_id,
                payload: response
              });
            }

            if (verifyResponse.success) {
              navigate('/aiota-member-profile', {
                state: {
                  renewalJustPaid: true,
                  membershipType,
                  orderId: response.razorpay_order_id,
                  paymentId: response.razorpay_payment_id
                }
              });
            } else {
              setError(verifyResponse.message || "Payment verification failed. Please contact support.");
            }
          } catch (error) {
            console.error("Payment verification error:", error);
            setError("Payment verification failed. Please contact support.");
          }
        },
        onFailure: (error) => {
          setError(error.message || "Payment failed. Please try again.");
        }
      });

    } catch (error) {
      console.error("Payment initiation error:", error);
      setError(error.message || "Failed to initiate payment. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (!acceptedTerms || !memberData || !paymentDetails) {
    return null; // Will redirect via useEffect
  }

  return (
    <Container className="py-5">
      <h2 className="mb-4">Membership Renewal Payment</h2>
      
      <Card className="mb-4">
        <Card.Body>
          <h5 className="mb-3">Payment Summary</h5>
          <div className="mb-3">
            <strong>Member Name:</strong> {memberData.name}<br />
            <strong>Membership Type:</strong> {membershipType === 'full' ? 'Life Member' : 'Associate Member'}<br />
          </div>
          
          <table className="table table-sm">
            <tbody>
              <tr>
                <td>Base Amount:</td>
                <td>{formatIndianCurrency(paymentDetails.breakdown.baseAmount)}</td>
              </tr>
              <tr>
                <td>GST (18%):</td>
                <td>{formatIndianCurrency(paymentDetails.breakdown.gst)}</td>
              </tr>
              {paymentDetails.breakdown.lateFee > 0 && (
                <>
                  <tr>
                    <td>Late Fee:</td>
                    <td>{formatIndianCurrency(paymentDetails.breakdown.lateFee)}</td>
                  </tr>
                  <tr>
                    <td>Late Fee GST:</td>
                    <td>{formatIndianCurrency(paymentDetails.breakdown.lateGst)}</td>
                  </tr>
                </>
              )}
              <tr className="table-primary">
                <td><strong>Total Amount:</strong></td>
                <td><strong>{formatIndianCurrency(paymentDetails.amount)}</strong></td>
              </tr>
            </tbody>
          </table>
        </Card.Body>
      </Card>

      {error && <Alert variant="danger" className="mb-4">{error}</Alert>}

      <div className="d-flex gap-2">
        <Button
          variant="primary"
          onClick={handlePayment}
          disabled={loading}
          className="d-flex align-items-center gap-2"
        >
          {loading ? 'Processing...' : 'Pay Now'}
        </Button>
        <Button
          variant="outline-secondary"
          onClick={() => navigate(-1)}
          disabled={loading}
        >
          Go Back
        </Button>
      </div>
    </Container>
  );
};

export default MembershipRenewalPayment;
